import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp } from "lucide-react";
import { api, errorMessage } from "@/lib/adminApi";

const STATUSES = [
    ["new", "New"],
    ["contacted", "Contacted"],
    ["contract_received", "Contract received"],
    ["closed", "Closed"],
];

function Detail({ label, value }) {
    return (
        <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
            <p className="mt-0.5 text-sm">{value || "—"}</p>
        </div>
    );
}

export default function JoinRequests({ items, onChanged }) {
    const [open, setOpen] = useState(null);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/join-requests/${id}`, { status });
            toast.success("Status updated");
            onChanged();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    if (items.length === 0) {
        return <p className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="join-requests-empty">No Join Forms yet.</p>;
    }

    return (
        <ul className="space-y-3" data-testid="join-requests-list">
            {items.map((j) => (
                <li key={j.id} className="rounded-2xl border border-border bg-surface" data-testid={`join-request-${j.id}`}>
                    <div className="flex flex-wrap items-center gap-4 p-5">
                        <div className="min-w-0 flex-1">
                            <p className="font-semibold">{j.salutation} {j.first_name} {j.last_name}{j.preferred_first_name ? ` (${j.preferred_first_name})` : ""}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                                {j.created_at.slice(0, 16).replace("T", " ")} UTC · {j.state} · {j.recruitment_company || "No recruitment company"}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${j.notified_at ? "bg-primary/10 text-primary" : "bg-surface-2 text-muted-foreground"}`}>
                                {j.notified_at ? "Emailed" : "Email pending"}
                            </span>
                            <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${j.crm_forwarded_at ? "bg-primary/10 text-primary" : "bg-surface-2 text-muted-foreground"}`} title={j.crm_error || ""}>
                                {j.crm_forwarded_at ? "CRM sent" : j.crm_error ? "CRM failed" : "CRM off"}
                            </span>
                            <select value={j.status} onChange={(e) => setStatus(j.id, e.target.value)} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold" data-testid={`join-status-${j.id}`}>
                                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <button onClick={() => setOpen(open === j.id ? null : j.id)} aria-label="Toggle details" data-testid={`join-toggle-${j.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground">
                                {open === j.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                        </div>
                    </div>
                    {open === j.id && (
                        <div className="grid gap-4 border-t border-border p-5 sm:grid-cols-2 lg:grid-cols-4" data-testid={`join-details-${j.id}`}>
                            <Detail label="Email" value={<a href={`mailto:${j.email}`} className="text-primary">{j.email}</a>} />
                            <Detail label="Mobile Number" value={j.mobile_number} />
                            <Detail label="Birthdate" value={j.date_of_birth} />
                            <Detail label="Company matched" value={j.recruitment_company_id ? "Yes" : "No"} />
                            <Detail label="Recruiter" value={[j.recruiter_first_name, j.recruiter_last_name].filter(Boolean).join(" ")} />
                            <Detail label="Recruiter Email" value={j.recruiter_email} />
                            <Detail label="Recruiter Phone Number" value={j.recruiter_phone_number} />
                            <Detail label="Where did you hear about us?" value={j.employment_contract_source} />
                            <Detail label="Referral Contractors Name" value={j.referred_from} />
                            <Detail label="Reference" value={<span className="font-mono text-xs">{j.id}</span>} />
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
}
